import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EXAM_TYPES } from "@/lib/constants";
import { Loader2, Plus, Trash2, ChevronLeft, ChevronRight, CalendarRange, CheckCircle2, X, Pencil } from "lucide-react";

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const monthName = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
};
const TYPE_CLS = { TYT: "bg-blue-100 text-blue-700 border-blue-200", AYT: "bg-amber-100 text-amber-700 border-amber-200" };

export default function ExamCalendar({ focusStudentId }) {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin";
  const canMark = role === "admin" || role === "coach";

  const [events, setEvents] = useState([]);
  const [students, setStudents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ title: "", date: "", exam_type: "", track: "", note: "" });
  const [examTracks, setExamTracks] = useState([]); // çoklu alan/sınıf (en fazla 3)
  const toggleExamTrack = (t) =>
    setExamTracks((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });
  const [busy, setBusy] = useState(false);
  const [marks, setMarks] = useState({});
  const [markOpen, setMarkOpen] = useState(null); // event id being marked

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/exam-events");
      setEvents(data);
      const withMarks = await Promise.all(data.map((e) => api.get(`/exam-events/${e.id}/marks`).then((r) => [e.id, r.data]).catch(() => [e.id, []])));
      setMarks(Object.fromEntries(withMarks));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (canMark) api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    if (isAdmin) api.get("/meta").then((r) => setTracks(r.data.tracks || [])).catch(() => {});
  }, [canMark, isAdmin]);

  const addEvent = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Sınav adı ve tarihi zorunlu");
    setBusy(true);
    try {
      const targets = examTracks.length ? examTracks : [""];
      for (const t of targets) {
        await api.post("/exam-events", { ...form, track: t });
      }
      toast.success(targets.length > 1 ? `Sınav ${targets.length} alan/sınıf için takvime eklendi` : "Sınav takvime eklendi");
      setForm({ title: "", date: "", exam_type: "", track: "", note: "" });
      setExamTracks([]);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const delEvent = async (id) => {
    if (!window.confirm("Bu sınav takvimden silinecek. Emin misiniz?")) return;
    try {
      await api.delete(`/exam-events/${id}`);
      toast.success("Sınav silindi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const [editEv, setEditEv] = useState(null);
  const saveEdit = async () => {
    if (!editEv.title.trim() || !editEv.date) return toast.error("Sınav adı ve tarihi zorunlu");
    try {
      await api.put(`/exam-events/${editEv.id}`, { title: editEv.title, date: editEv.date, exam_type: editEv.exam_type || "", track: editEv.track || "", note: editEv.note || "" });
      toast.success("Sınav güncellendi");
      setEditEv(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const markOf = (eid, sid) => (marks[eid] || []).find((m) => m.student_id === sid);

  const toggleMark = async (ev, sid) => {
    const existing = markOf(ev.id, sid);
    try {
      if (existing) {
        await api.delete(`/exam-events/${ev.id}/mark/${sid}`);
      } else {
        await api.post(`/exam-events/${ev.id}/mark`, { student_id: sid });
      }
      const { data } = await api.get(`/exam-events/${ev.id}/marks`);
      setMarks((m) => ({ ...m, [ev.id]: data }));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const shiftMonth = (d) => {
    const [y, m] = ym.split("-").map(Number);
    const nd = new Date(y, m - 1 + d, 1);
    setYm(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
  };

  const [year, month] = ym.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const eventsFor = (day) => {
    const key = `${ym}-${String(day).padStart(2, "0")}`;
    return events.filter((e) => (e.date || "") === key);
  };

  // öğrenci/veli odak görünümü: kendi işareti varsa renk değişir
  const focusMark = (eid) => {
    if (!focusStudentId) return null;
    return markOf(eid, focusStudentId);
  };

  return (
    <div className="space-y-6" data-testid="exam-calendar">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900">Sınav Takvimi</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          {isAdmin ? "Yıl boyunca yapılacak sınavları aylık takvime işleyin; koç ve siz sınava giren öğrencileri işaretleyin." : "Yıl boyunca yapılacak sınavlar aylık takvimde görünür."}
        </p>
      </div>

      {isAdmin && (
        <div className="card-ta p-5" data-testid="exam-add">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2">
              <label className="ta-label">Sınav Adı</label>
              <input className="ta-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="örn. TYT Genel Deneme 1" data-testid="exam-title" />
            </div>
            <div>
              <label className="ta-label">Tarih</label>
              <input type="date" className="ta-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exam-date" />
            </div>
            <div>
              <label className="ta-label">Tür</label>
              <select className="ta-input" value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })} data-testid="exam-type">
                <option value="">Seçiniz</option>
                {EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className="ta-label">Alan / Sınıf (ops. — en fazla 3, boş = tümü)</label>
              <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-white p-2" data-testid="exam-track-chips">
                {tracks.map((t) => (
                  <button type="button" key={t} onClick={() => toggleExamTrack(t)} data-testid={`exam-track-chip-${t}`}
                    className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${examTracks.includes(t) ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button onClick={addEvent} disabled={busy} className="mt-3 bg-indigo-900 hover:bg-indigo-800" data-testid="exam-add-btn">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Takvime Ekle</span>
          </Button>
        </div>
      )}

      {isAdmin && !loading && (
        <div className="card-ta overflow-hidden" data-testid="exam-admin-list">
          <div className="px-4 py-3 border-b border-slate-200 font-display font-bold text-slate-900">Açılan Sınavlar ({events.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200"><th className="px-4 py-2">Tarih</th><th className="px-4 py-2">Sınav</th><th className="px-4 py-2">Tür</th><th className="px-4 py-2">Alan / Sınıf</th><th className="px-4 py-2">Giren</th><th className="px-4 py-2" /></tr></thead>
              <tbody>
                {events.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Henüz sınav açılmadı.</td></tr>}
                {events.map((ev) => editEv?.id === ev.id ? (
                  <tr key={ev.id} className="border-b border-slate-100 bg-amber-50" data-testid={`exam-row-edit-${ev.id}`}>
                    <td className="px-2 py-2"><input type="date" className="ta-input !py-1" value={editEv.date} onChange={(e) => setEditEv({ ...editEv, date: e.target.value })} data-testid="exam-edit-date" /></td>
                    <td className="px-2 py-2"><input className="ta-input !py-1" value={editEv.title} onChange={(e) => setEditEv({ ...editEv, title: e.target.value })} data-testid="exam-edit-title" /></td>
                    <td className="px-2 py-2"><select className="ta-input !py-1" value={editEv.exam_type || ""} onChange={(e) => setEditEv({ ...editEv, exam_type: e.target.value })} data-testid="exam-edit-type"><option value="">—</option>{EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                    <td className="px-2 py-2"><select className="ta-input !py-1" value={editEv.track || ""} onChange={(e) => setEditEv({ ...editEv, track: e.target.value })} data-testid="exam-edit-track"><option value="">Tümü</option>{tracks.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                    <td className="px-4 py-2 text-slate-500">{(marks[ev.id] || []).length}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <Button size="sm" onClick={saveEdit} className="h-7 text-xs bg-indigo-900 hover:bg-indigo-800 mr-1" data-testid="exam-edit-save">Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditEv(null)} className="h-7 text-xs" data-testid="exam-edit-cancel">Vazgeç</Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={ev.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`exam-row-${ev.id}`}>
                    <td className="px-4 py-2 font-mono text-slate-700">{ev.date}</td>
                    <td className="px-4 py-2 font-semibold text-slate-900">{ev.title}</td>
                    <td className="px-4 py-2">{ev.exam_type ? <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${TYPE_CLS[ev.exam_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{ev.exam_type}</span> : "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{ev.track || "Tümü"}</td>
                    <td className="px-4 py-2 text-slate-600">{(marks[ev.id] || []).length}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setEditEv({ ...ev })} className="text-slate-500 hover:text-indigo-700 mr-3" title="Düzelt" data-testid={`exam-row-edit-btn-${ev.id}`}><Pencil size={15} /></button>
                      <button onClick={() => delEvent(ev.id)} className="text-slate-500 hover:text-rose-600" title="Sil" data-testid={`exam-row-del-${ev.id}`}><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card-ta p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-700 font-semibold"><CalendarRange size={18} /> {monthName(ym)}</div>
          <div className="flex gap-1">
            <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="exam-prev-month"><ChevronLeft size={18} /></button>
            <button onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="exam-next-month"><ChevronRight size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1.5" data-testid="exam-grid">
            {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-bold text-slate-400 uppercase py-1">{w}</div>)}
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const evs = eventsFor(day);
              return (
                <div key={day} className="min-h-[86px] rounded-lg border border-slate-100 bg-slate-50/60 p-1.5" data-testid={`exam-day-${day}`}>
                  <div className="text-[11px] font-bold text-slate-400">{day}</div>
                  <div className="space-y-1 mt-0.5">
                    {evs.map((ev) => {
                      const fm = focusMark(ev.id);
                      const marked = (marks[ev.id] || []).length;
                      let cls = "bg-white border-l-indigo-500";
                      const passed = (ev.date || "") <= new Date().toISOString().slice(0, 10);
                      if (fm?.status === "Onaylı") cls = "bg-emerald-100 border-l-emerald-600";
                      else if (fm) cls = "bg-blue-100 border-l-blue-600";
                      else if (focusStudentId && passed) cls = "bg-rose-100 border-l-rose-600";
                      return (
                        <div key={ev.id} className={`rounded-md border-l-2 ${cls} px-1.5 py-1 text-[10px] leading-tight`} data-testid={`exam-item-${ev.id}`}>
                          <div className="font-bold text-slate-700 truncate flex items-center gap-1">
                            {ev.exam_type && <span className={`px-1 rounded border text-[9px] ${TYPE_CLS[ev.exam_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{ev.exam_type}</span>}
                            <span className="truncate">{ev.title}</span>
                          </div>
                          {ev.track && <div className="text-slate-400 truncate">{ev.track}</div>}
                          <div className="flex items-center gap-1 mt-0.5">
                            {canMark && (
                              <button onClick={() => setMarkOpen(markOpen === ev.id ? null : ev.id)} className="text-[9px] text-indigo-600 hover:underline" data-testid={`exam-mark-toggle-${ev.id}`}>
                                İşaretle ({marked})
                              </button>
                            )}
                            {fm && <span className="text-[9px] font-semibold text-emerald-700 flex items-center gap-0.5"><CheckCircle2 size={9} />{fm.status === "Onaylı" ? "Onaylı" : "Girdi"}</span>}
                            {!fm && focusStudentId && passed && <span className="text-[9px] font-bold text-rose-600" data-testid={`exam-missed-${ev.id}`}>Girmedi</span>}
                            {isAdmin && (
                              <button onClick={() => delEvent(ev.id)} className="text-slate-300 hover:text-rose-600 ml-auto" data-testid={`exam-del-${ev.id}`}><Trash2 size={10} /></button>
                            )}
                          </div>
                          {markOpen === ev.id && canMark && (
                            <div className="mt-1 max-h-28 overflow-y-auto rounded border border-slate-200 bg-white" data-testid={`exam-mark-list-${ev.id}`}>
                              {students.map((s) => {
                                const mk = markOf(ev.id, s.id);
                                return (
                                  <button key={s.id} onClick={() => toggleMark(ev, s.id)} data-testid={`exam-mark-${ev.id}-${s.id}`}
                                    className={`w-full flex items-center justify-between px-1.5 py-1 text-[10px] hover:bg-slate-50 ${mk ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-600"}`}>
                                    <span className="truncate">{s.name}</span>
                                    {mk ? <CheckCircle2 size={11} /> : <X size={11} className="text-slate-300" />}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
